import { useEffect, useId, useLayoutEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useRoles } from '@/features/roles/hooks/useRoles'
import { useOrgCurrency } from '@/hooks/useOrgCurrency'
import { Button, DateInput, Input, Select, Spinner, useModalFooterSlot } from '@/components/ui'
import type { SelectOption } from '@/components/ui'
import { useCreateUser } from '../hooks/useUsers'
import {
  createUserSchema,
  formValuesToCreateUserPayload,
  type CreateUserFormValues,
} from '../schemas/createUser.schemas'

// TODO(onboarding): additional profile fields (personal info, full address, bank/tax,
// emergency contact, department/designation, etc.) are collected on the user's first-login
// onboarding screen rather than at admin-create time.

const workLocationOptions: SelectOption[] = [
  { value: 'onsite', label: 'Onsite' },
  { value: 'remote', label: 'Remote' },
  { value: 'hybrid', label: 'Hybrid' },
]

const compensationTypeOptions: SelectOption[] = [
  { value: 'salaried', label: 'Salaried (fixed monthly pay — managers)' },
  { value: 'hourly', label: 'Hourly (paid per clocked hour — staff)' },
]

export interface CreateUserFormProps {
  onSuccess?: (newUserId: string, email: string) => void
  onCancel?: () => void
  /** When provided, the form's <form> uses this id so an external submit button can target it. */
  externalFormId?: string
  /** When true, the inline submit button is hidden so the parent can render its own footer. */
  hideFooter?: boolean
  /** Notifies the parent whenever the create mutation's pending state changes, so an
   * external submit button (e.g. CreateUserPage's aside) can disable itself in sync. */
  onPendingChange?: (isPending: boolean) => void
}

export function CreateUserForm({
  onSuccess,
  onCancel,
  externalFormId,
  hideFooter,
  onPendingChange,
}: CreateUserFormProps): React.ReactElement {
  const localFormId = useId()
  const formId = externalFormId ?? localFormId
  const setModalFooter = useModalFooterSlot()
  const { data: roles = [], isLoading: rolesLoading } = useRoles()
  const { currency } = useOrgCurrency()
  const createUser = useCreateUser()

  const roleOptions: SelectOption[] = roles.map((r) => ({ value: r._id, label: r.name }))

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    clearErrors,
  } = useForm<CreateUserFormValues>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      roleId: '',
      joiningDate: '',
      workLocation: 'onsite',
      compensationType: 'salaried',
      baseSalary: Number.NaN,
    },
  })

  useEffect(() => {
    onPendingChange?.(createUser.isPending)
  }, [createUser.isPending, onPendingChange])

  const roleId = watch('roleId')
  const workLocation = watch('workLocation')
  const compensationType = watch('compensationType')

  const onSubmit = (values: CreateUserFormValues): void => {
    // Guard against a second submit slipping through before the button's disabled
    // state applies (e.g. a fast double-click), which would create a duplicate user.
    if (createUser.isPending) return
    const payload = formValuesToCreateUserPayload(values, currency)
    createUser.mutate(payload, {
      onSuccess: (user) => {
        reset()
        onSuccess?.(user._id, user.email)
      },
    })
  }

  useLayoutEffect(() => {
    if (!setModalFooter) return
    setModalFooter(
      <>
        {onCancel != null && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button form={formId} type="submit" loading={createUser.isPending}>
          {createUser.isPending ? 'Creating…' : 'Create user'}
        </Button>
      </>
    )
    return () => setModalFooter(null)
  }, [setModalFooter, onCancel, createUser.isPending, formId])

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
      <p className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-gray-800/40 dark:text-gray-400">
        The new user will receive a secure password setup email after creation and can finish
        signing in from that link.
      </p>

      <Input
        label="First name"
        required
        {...register('firstName')}
        error={errors.firstName?.message}
        placeholder="John"
      />
      <Input
        label="Last name"
        required
        {...register('lastName')}
        error={errors.lastName?.message}
        placeholder="Doe"
      />
      <Input
        label="Email"
        type="email"
        required
        {...register('email')}
        error={errors.email?.message}
        placeholder="user@example.com"
      />

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Role <span className="text-danger"> *</span>
        </label>
        {rolesLoading ? (
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 py-2 px-3 dark:border-gray-700 dark:bg-gray-800/50">
            <Spinner size="sm" />
            <span className="text-sm text-gray-600 dark:text-gray-400">Loading roles…</span>
          </div>
        ) : (
          <Select
            options={roleOptions}
            value={roleId}
            onChange={(v) => setValue('roleId', v, { shouldDirty: true, shouldValidate: true })}
            placeholder="Select role"
          />
        )}
        {errors.roleId && <p className="mt-1 text-sm text-danger">{errors.roleId.message}</p>}
      </div>

      <DateInput
        label="Joining date"
        {...register('joiningDate')}
        error={errors.joiningDate?.message}
      />

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Work location <span className="text-danger"> *</span>
        </label>
        <Select
          options={workLocationOptions}
          value={workLocation}
          onChange={(v) =>
            setValue('workLocation', v as CreateUserFormValues['workLocation'], {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        />
        {errors.workLocation && (
          <p className="mt-1 text-sm text-danger">{errors.workLocation.message}</p>
        )}
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Compensation type <span className="text-danger"> *</span>
        </label>
        <Select
          options={compensationTypeOptions}
          value={compensationType}
          onChange={(v) => {
            // Switching the discriminator clears the now-invalid rate field
            // so stale values don't hang around and trip XOR validation.
            if (v === 'hourly') {
              setValue('hourlyRate', Number.NaN, { shouldValidate: false })
              setValue('compensationType', 'hourly', {
                shouldDirty: true,
                shouldValidate: false,
              })
            } else {
              setValue('baseSalary', Number.NaN, { shouldValidate: false })
              setValue('compensationType', 'salaried', {
                shouldDirty: true,
                shouldValidate: false,
              })
            }
            clearErrors(['compensationType', 'hourlyRate', 'baseSalary'])
          }}
        />
        {errors.compensationType && (
          <p className="mt-1 text-sm text-danger">{errors.compensationType.message}</p>
        )}
      </div>

      {compensationType === 'hourly' ? (
        <Input
          label={`Hourly rate (${currency})`}
          type="number"
          step="0.01"
          required
          {...register('hourlyRate', { valueAsNumber: true })}
          error={
            errors.compensationType === undefined && 'hourlyRate' in errors
              ? (errors as { hourlyRate?: { message?: string } }).hourlyRate?.message
              : undefined
          }
          placeholder="0.00"
        />
      ) : (
        <Input
          label={`Base salary (${currency})`}
          type="number"
          step="0.01"
          required
          {...register('baseSalary', { valueAsNumber: true })}
          error={
            errors.compensationType === undefined && 'baseSalary' in errors
              ? (errors as { baseSalary?: { message?: string } }).baseSalary?.message
              : undefined
          }
          placeholder="0.00"
        />
      )}
      {!setModalFooter && !hideFooter && (
        <Button type="submit" loading={createUser.isPending}>
          {createUser.isPending ? 'Creating…' : 'Create user'}
        </Button>
      )}
    </form>
  )
}

CreateUserForm.displayName = 'CreateUserForm'
